"use client";

import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { ImageIcon, Search, Smile, X } from "lucide-react";
import {
  KLIPY_GIF_ALT,
  KLIPY_GIF_TOKEN_PATTERN,
  isTrustedKlipyMediaUrl,
  klipyGifToken,
} from "@/lib/klipy";
import type { KlipyGifResult } from "@/lib/klipy";
import { cn } from "@/lib/utils";

type GifSearchResult = KlipyGifResult;
type GifPopoverPosition = {
  left: number;
  top: number;
  width: number;
  maxHeight: number;
  placement: "up" | "down";
};

const VISIBLE_EMOJIS = ["😀", "💬", "🗣️", "📣", "📰", "🎙️", "📌", "🔥", "⚽"];
const PICKER_EMOJIS = ["😀", "😉", "😂", "💩", "☠️", "🦴", "🤡", "😵", "🚩", "🏳️", "🇭🇺", "🎯", "🚨", "❗"];
const MENTION_PATTERN = /@([\p{L}\p{N}_.-]+(?:\s#\d{4})?)/gu;
const GIF_POPOVER_GAP = 8;
const GIF_POPOVER_VIEWPORT_MARGIN = 12;
const GIF_POPOVER_DESKTOP_WIDTH = 380;
const GIF_POPOVER_DESKTOP_MAX_HEIGHT = 360;
const GIF_POPOVER_MOBILE_MAX_HEIGHT = 420;
const GIF_POPOVER_MIN_USABLE_HEIGHT = 280;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function calculateGifPopoverPosition(anchor: HTMLElement): GifPopoverPosition {
  const rect = anchor.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const margin = GIF_POPOVER_VIEWPORT_MARGIN;
  const isMobile = viewportWidth < 640;
  const maxPreferredHeight = isMobile ? GIF_POPOVER_MOBILE_MAX_HEIGHT : GIF_POPOVER_DESKTOP_MAX_HEIGHT;
  const width = Math.min(isMobile ? viewportWidth - margin * 2 : GIF_POPOVER_DESKTOP_WIDTH, viewportWidth - margin * 2);
  const spaceBelow = viewportHeight - rect.bottom - GIF_POPOVER_GAP - margin;
  const spaceAbove = rect.top - GIF_POPOVER_GAP - margin;
  const placement = spaceBelow >= GIF_POPOVER_MIN_USABLE_HEIGHT || spaceBelow >= spaceAbove ? "down" : "up";
  const availableHeight = Math.max(160, placement === "down" ? spaceBelow : spaceAbove);
  const maxHeight = Math.min(maxPreferredHeight, availableHeight);
  const left = isMobile
    ? margin
    : clamp(rect.right - width, margin, Math.max(margin, viewportWidth - width - margin));
  const top =
    placement === "down"
      ? Math.min(rect.bottom + GIF_POPOVER_GAP, viewportHeight - margin - maxHeight)
      : Math.max(margin, rect.top - GIF_POPOVER_GAP - maxHeight);

  return { left, top, width, maxHeight, placement };
}

export function CommentRichContent({
  content,
  className = "mt-4 whitespace-pre-wrap text-sm leading-6 text-[#273E47]",
}: {
  content: string;
  className?: string;
}) {
  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let keyIndex = 0;

  function pushTextSegment(segment: string) {
    let mentionLastIndex = 0;
    for (const match of segment.matchAll(MENTION_PATTERN)) {
      const token = match[0];
      const mention = match[1];
      const index = match.index ?? 0;
      if (index > mentionLastIndex) {
        parts.push(segment.slice(mentionLastIndex, index));
      }

      const maybeUsername = mention.includes(" ") ? null : mention;
      parts.push(
        maybeUsername ? (
          <Link
            key={`mention-${mention}-${keyIndex++}`}
            href={`/u/${maybeUsername}`}
            className="font-bold text-[#A4243B] underline-offset-4 hover:underline"
          >
            {token}
          </Link>
        ) : (
          <span key={`mention-${mention}-${keyIndex++}`} className="font-bold text-[#A4243B]">
            {token}
          </span>
        ),
      );
      mentionLastIndex = index + token.length;
    }
    if (mentionLastIndex < segment.length) {
      parts.push(segment.slice(mentionLastIndex));
    }
  }

  for (const match of content.matchAll(KLIPY_GIF_TOKEN_PATTERN)) {
    const token = match[0];
    const url = match[1]?.trim() ?? "";
    const index = match.index ?? 0;

    if (index > lastIndex) {
      pushTextSegment(content.slice(lastIndex, index));
    }

    if (isTrustedKlipyMediaUrl(url)) {
      parts.push(
        <span key={`${url}-${index}`} className="my-3 block">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={url}
            alt={KLIPY_GIF_ALT}
            loading="lazy"
            decoding="async"
            className="max-h-72 max-w-full rounded-[18px] border border-black/5 bg-[#FCFBF8] object-contain shadow-sm"
          />
        </span>,
      );
    }

    lastIndex = index + token.length;
  }

  if (lastIndex < content.length) {
    pushTextSegment(content.slice(lastIndex));
  }

  return <p className={className}>{parts}</p>;
}

function GifSearchPopover({ onSelect }: { onSelect: (gif: GifSearchResult) => void }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<GifSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [position, setPosition] = useState<GifPopoverPosition | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    setPosition(calculateGifPopoverPosition(buttonRef.current));
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();

    function handlePointerDown(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (buttonRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }

    function handleViewportChange() {
      updatePosition();
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) return;
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      const resetTimer = window.setTimeout(() => {
        setResults([]);
        setError(null);
        setLoading(false);
      }, 0);
      return () => window.clearTimeout(resetTimer);
    }

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      fetch(`/api/gifs/search?q=${encodeURIComponent(trimmed)}`, {
        signal: controller.signal,
        headers: { accept: "application/json" },
      })
        .then(async (response) => {
          const payload = (await response.json()) as { results?: GifSearchResult[]; error?: string };
          if (!response.ok) throw new Error(payload.error ?? "gif_search_failed");
          setResults((payload.results ?? []).filter((item) => isTrustedKlipyMediaUrl(item.url)).slice(0, 12));
        })
        .catch((fetchError) => {
          if (fetchError instanceof DOMException && fetchError.name === "AbortError") return;
          setResults([]);
          setError("Most nem elérhető a GIF kereső.");
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 250);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [open, query]);

  const popover = open && position ? (
    <div
      ref={popoverRef}
      data-placement={position.placement}
      className="fixed z-[1000] flex flex-col rounded-[18px] border border-[#D8C99B]/70 bg-[#FCFBF8] p-3 shadow-xl"
      style={{
        left: position.left,
        top: position.top,
        width: position.width,
        maxHeight: position.maxHeight,
      }}
    >
      <div className="flex items-center gap-2 border-b border-[#D8C99B]/60 pb-2">
        <Search size={15} className="shrink-0 text-[#273E47]/55" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search KLIPY"
          className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-[#273E47] outline-none placeholder:text-[#273E47]/45"
          autoFocus
        />
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="GIF kereső bezárása"
          className="grid h-7 w-7 place-items-center rounded-full text-[#273E47]/60 transition hover:bg-[#EEF0EA] hover:text-[#273E47] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20"
        >
          <X size={14} />
        </button>
      </div>

      <div className="mt-3 min-h-0 overflow-y-auto pr-1">
        {query.trim().length < 2 ? (
          <div className="flex min-h-28 items-center justify-center rounded-[14px] bg-[#F8F6F0] px-4 text-center text-xs font-semibold text-[#273E47]/60">
            Írj be legalább két karaktert a KLIPY GIF kereséshez.
          </div>
        ) : loading ? (
          <div className="grid min-h-28 place-items-center rounded-[14px] bg-[#F8F6F0] text-xs font-black uppercase tracking-[0.14em] text-[#273E47]/55">
            Keresés...
          </div>
        ) : error ? (
          <div className="flex min-h-28 items-center justify-center rounded-[14px] bg-[#F8F6F0] px-4 text-center text-xs font-semibold text-[#A4243B]">
            {error}
          </div>
        ) : results.length ? (
          <div className="grid grid-cols-3 gap-2">
            {results.map((gif) => (
              <button
                key={`${gif.id}-${gif.url}`}
                type="button"
                onClick={() => {
                  onSelect(gif);
                  setOpen(false);
                }}
                title={gif.title}
                className="aspect-square overflow-hidden rounded-[12px] border border-black/5 bg-[#F8F6F0] transition hover:border-[#A4243B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={gif.previewUrl} alt={gif.title} loading="lazy" decoding="async" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        ) : (
          <div className="flex min-h-28 items-center justify-center rounded-[14px] bg-[#F8F6F0] px-4 text-center text-xs font-semibold text-[#273E47]/60">
            Nincs találat.
          </div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="inline-flex h-9 items-center gap-1.5 rounded-full border border-[#D8C99B]/70 bg-[#FCFBF8] px-3 text-xs font-black uppercase text-[#273E47]/75 transition hover:border-[#A4243B] hover:text-[#273E47] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20"
      >
        <ImageIcon size={14} />
        GIF
      </button>

      {popover && typeof document !== "undefined" ? createPortal(popover, document.body) : null}
    </>
  );
}

function EmojiPickerPopover({ onSelect }: { onSelect: (emoji: string) => void }) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;

    function handlePointerDown(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (buttonRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="További emojik"
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-transparent text-xl transition-all duration-150 hover:scale-110 hover:bg-[#A4243B]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20",
          open && "border-[#A4243B]/35 bg-[#A4243B]/10 text-[#A4243B] shadow-[0_8px_22px_rgba(39,62,71,0.10)]",
        )}
      >
        <Smile size={13} />
      </button>

      {open ? (
        <div
          ref={popoverRef}
          role="dialog"
          aria-label="Emoji választó"
          className="absolute left-0 top-full z-[70] mt-2 grid w-[min(15.5rem,calc(100vw-2rem))] grid-cols-5 gap-0.5 rounded-3xl border border-[#A4243B]/30 bg-[#FCFBF8] p-2 shadow-[0_12px_32px_rgba(39,62,71,0.12)]"
        >
          {PICKER_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => {
                onSelect(emoji);
                setOpen(false);
              }}
              title={emoji}
              aria-label={`Emoji beszúrása: ${emoji}`}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-xl transition-all duration-150 hover:scale-125 hover:bg-[#A4243B]/10 active:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20"
            >
              {emoji}
            </button>
          ))}
        </div>
      ) : null}
    </>
  );
}

export function CommentComposerTools({ onInsert }: { onInsert: (value: string) => void }) {
  return (
    <div className="relative flex min-w-0 max-w-full flex-nowrap items-center gap-1.5 overflow-visible max-sm:w-full">
      <EmojiPickerPopover onSelect={onInsert} />
      <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto pb-1">
        {VISIBLE_EMOJIS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => onInsert(emoji)}
            title={emoji}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-xl transition-all duration-150 hover:scale-110 hover:bg-[#F6F1E3] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A4243B]/20"
          >
            {emoji}
          </button>
        ))}
      </div>
      <GifSearchPopover
        onSelect={(gif) => {
          const token = klipyGifToken(gif.url);
          if (token) onInsert(token);
        }}
      />
    </div>
  );
}
